import logoMark from "@/assets/logo-mark.png";
import logoMarkLight from "@/assets/logo-mark-light.png";
import logoWide from "@/assets/logo-wide-dark.png";
import logoWideLight from "@/assets/logo-wide-light.png";
import logoTall from "@/assets/logo-tall-dark.png";
import logoTallLight from "@/assets/logo-tall-light.png";

type BrandLogoVariant = "wide" | "tall" | "mark";
type BrandLogoTone = "dark" | "light";

interface BrandLogoProps {
  variant?: BrandLogoVariant;
  tone?: BrandLogoTone;
  className?: string;
  alt?: string;
}

/**
 * Stage Studios brand lockup. Per brand guidance the wordmark MUST stay
 * with the icon — only `variant="mark"` (icon alone) is acceptable, and
 * only for the sidebar-collapsed slot or favicon contexts.
 *
 *   variant="wide" (default) — icon to the LEFT of the wordmark. Use in
 *                              nav bars, footers, mobile top-bar, sidebar
 *                              header — anywhere wide-and-short.
 *   variant="tall"           — icon ABOVE the wordmark. Use in centred
 *                              auth panels, hero blocks, 404 page —
 *                              anywhere a square-ish lockup fits better.
 *   variant="mark"           — icon ONLY. Reserved for the sidebar
 *                              collapsed state and browser favicon. Avoid
 *                              elsewhere.
 *
 *   tone="dark"  → black-ink for light backgrounds (default)
 *   tone="light" → white-ink for dark backgrounds
 *
 * `className` is forwarded to the <img> so callers can pick a size with
 * Tailwind utilities (e.g. `h-8 w-auto`).
 */
export default function BrandLogo({
  variant = "wide",
  tone = "dark",
  className = "h-8 w-auto",
  alt = "Stage Studios",
}: BrandLogoProps) {
  let src: string;
  if (variant === "tall") {
    src = tone === "light" ? logoTallLight : logoTall;
  } else if (variant === "mark") {
    src = tone === "light" ? logoMarkLight : logoMark;
  } else {
    // default: wide
    src = tone === "light" ? logoWideLight : logoWide;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      draggable={false}
      data-testid={`brand-logo-${variant}-${tone}`}
    />
  );
}
